/**
 * Transform UI filter groups into Gelly query syntax for computed views
 * @param {Array} filterGroups - Array of filter groups from ChipsContactFilter
 * @returns {string} - Gelly query string
 */
export const transformFiltersToGelly = (filterGroups = []) => {
  if (!filterGroups || filterGroups.length === 0) {
    return `{
  contacts {
    totalCount: count(1)
  }
}`;
  }

  // Transform each group (AND logic between groups)
  const groupConditions = filterGroups
    .map((group) => {
      if (!group.ors || group.ors.length === 0) {
        return null;
      }

      // Transform OR conditions within a group
      const orConditions = group.ors
        .map((condition) => {
          return transformSingleFilter(condition);
        })
        .filter(Boolean);

      if (orConditions.length === 0) {
        return null;
      }

      // If only one condition, return it directly
      if (orConditions.length === 1) {
        return orConditions[0];
      }

      // Multiple OR conditions
      return `(${orConditions.join(" || ")})`;
    })
    .filter(Boolean);

  if (groupConditions.length === 0) {
    return `{
  contacts {
    totalCount: count(1)
  }
}`;
  }

  // Build the where clause
  let whereClause;
  if (groupConditions.length === 1) {
    whereClause = groupConditions[0];
  } else {
    // Multiple AND groups
    whereClause = groupConditions.join(" && ");
  }

  return `{
  contacts {
    totalCount: count(1)
    [where ${whereClause}]
  }
}`;
};

/**
 * Transform a single filter condition to Gelly syntax
 * @param {Object} condition - Single filter condition
 * @returns {string|null} - Gelly condition string or null if invalid
 */

const transformSingleFilter = (condition) => {
  const {
    type,
    operator,
    value,
    customFieldName,
    tagId,
    frequency,
    campaignType,
    campaignId,
  } = condition;

  // Skip if no value (except for isSet and overAllTime operators)
  // Allow false as a valid value
  if (
    (value === undefined ||
      value === null ||
      (typeof value == typeof "string" && value.trim() === "")) &&
    operator !== "isSet" &&
    operator !== "overAllTime"
  ) {
    return null;
  }

  // Handle First Name filter
  if (type === "firstName") {
    if (operator === "is") {
      return `firstName == "${value}"`;
    } else if (operator === "isNot") {
      return `(firstName != "${value}" || isNull(firstName))`;
    }
  }

  // Handle WhatsApp Name filter
  if (type === "whatsappName") {
    if (operator === "is") {
      return `whatsappName == "${value}"`;
    } else if (operator === "isNot") {
      return `(whatsappName != "${value}" || isNull(whatsappName))`;
    }
  }

  // Handle Last Name filter
  if (type === "lastName") {
    if (operator === "is") {
      return `lastName == "${value}"`;
    } else if (operator === "isNot") {
      return `(lastName != "${value}" || isNull(lastName))`;
    }
  }

  // Handle Phone filter
  if (type === "phone") {
    if (operator === "equals") {
      return `userPhoneNumber == "${value}"`;
    } else if (operator === "startsWith") {
      return `startsWith(userPhoneNumber, prefix: "${value}")`;
    } else if (operator === "isSet") {
      return `!isNull(userPhoneNumber) && userPhoneNumber != ""`;
    }
  }

  // Handle Optin Status filter (hasOptIn + optoutAt)
  if (type === "optinStatus") {
    if (operator === "is") {
      if (value === "Optin") {
        return `hasOptIn == true`;
      } else if (value === "Never optin") {
        return `hasOptIn == false && isNull(optoutAt)`;
      } else if (value === "Optout") {
        return `hasOptIn == false && !isNull(optoutAt)`;
      }
    } else if (operator === "isNot") {
      if (value === "Optin") {
        return `hasOptIn == false`;
      } else if (value === "Never optin") {
        return `(hasOptIn == true || !isNull(optoutAt))`;
      } else if (value === "Optout") {
        // "is not optout" means: hasOptIn is true OR optoutAt is not set
        return `(hasOptIn == true || isNull(optoutAt))`;
      }
    }
  }

  // Handle Tag filter (relationship query)
  // Using the new syntax: some(tags.id == "tagId") and every(tags.id != "tagId")
  if (type === "tag") {
    if (operator === "contains") {
      // Use tagId if available, otherwise fall back to value
      const tagIdentifier = tagId || value;
      if (!tagIdentifier) {
        return null;
      }

      return `some(tags.id == "${tagIdentifier}")`;
    } else if (operator === "excludes") {
      // Use tagId if available, otherwise fall back to value
      const tagIdentifier = tagId || value;
      if (!tagIdentifier) {
        return null;
      }

      return `every(tags.id != "${tagIdentifier}")`;
    }
  }

  // Handle Custom Field filter (JSON field)
  // Using JSON functions from Gelly documentation
  if (type === "customField") {
    if (
      operator === "is" &&
      customFieldName &&
      value !== undefined &&
      value !== null
    ) {
      // Use contains() function with named parameters
      // Reference: https://docs.gadget.dev/reference/gelly#contains-value-json-item-string--json-boolean
      // Handle boolean values without quotes
      const formattedValue = typeof value === "boolean" ? value : `"${value}"`;
      return `contains(value: customProperties, item: {${customFieldName}: ${formattedValue}})`;
    } else if (
      operator === "isNot" &&
      customFieldName &&
      value !== undefined &&
      value !== null
    ) {
      // For "is not", we need to check that the field either doesn't exist or has a different value
      // This includes contacts where:
      // 1. customProperties is empty/null
      // 2. customProperties doesn't contain the field
      // 3. customProperties contains the field but with a different value
      // Handle boolean values without quotes
      const formattedValue = typeof value === "boolean" ? value : `"${value}"`;
      return `(isNull(customProperties) || customProperties == {} || !contains(value: customProperties, item: {${customFieldName}: ${formattedValue}}))`;
    }
  }

  // Helper function to generate date filter for message/URL filters
  const generateDateFilter = (operator, value, dateField) => {
    if (operator === "overAllTime") {
      // Treat "over all time" as "more than -1 day ago" (before tomorrow)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const gellyDate = tomorrow.toISOString().split("T")[0];
      return `dateTrunc(part: "day", date: ${dateField}) < date("${gellyDate}")`;
    }

    if (operator === "moreThan") {
      const daysAgo = parseInt(value);
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - daysAgo + 1);
      targetDate.setHours(0, 0, 0, 0);
      // For "more than X days ago", we want records before the target date
      const gellyDate = targetDate.toISOString().split("T")[0];
      return `dateTrunc(part: "day", date: ${dateField}) < date("${gellyDate}")`;
    } else if (operator === "lessThan") {
      const daysAgo = parseInt(value);
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - daysAgo + 1);
      targetDate.setHours(0, 0, 0, 0);
      // For "less than X days ago", we want records from the target date onwards
      const gellyDate = targetDate.toISOString().split("T")[0];
      return `dateTrunc(part: "day", date: ${dateField}) >= date("${gellyDate}")`;
    } else if (operator === "after") {
      // For "after", if the time is not 00:00:00, we need to add a day to the Gelly query
      const targetDate = new Date(value);
      const isMidnight =
        targetDate.getHours() === 0 &&
        targetDate.getMinutes() === 0 &&
        targetDate.getSeconds() === 0;
      const gellyDate = isMidnight
        ? value
        : new Date(targetDate.getTime() + 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
      return `dateTrunc(part: "day", date: ${dateField}) >= date("${gellyDate}")`;
    } else if (operator === "before") {
      // For "before", if the time is not 00:00:00, we need to subtract a day from the Gelly query
      const targetDate = new Date(value);
      const isMidnight =
        targetDate.getHours() === 0 &&
        targetDate.getMinutes() === 0 &&
        targetDate.getSeconds() === 0;
      const gellyDate = isMidnight
        ? value
        : new Date(targetDate.getTime() - 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
      return `dateTrunc(part: "day", date: ${dateField}) < date("${gellyDate}")`;
    } else if (operator === "on") {
      // For "on", we need to handle the time component by adjusting the date range
      const targetDate = new Date(value);
      const isStartOfDay =
        targetDate.getHours() === 0 &&
        targetDate.getMinutes() === 0 &&
        targetDate.getSeconds() === 0;
      const isEndOfDay =
        targetDate.getHours() === 23 &&
        targetDate.getMinutes() === 59 &&
        targetDate.getSeconds() === 59;

      if (isStartOfDay) {
        // If it's start of day, include the whole day
        const endDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];
        return `dateTrunc(part: "day", date: ${dateField}) >= date("${value}") && dateTrunc(part: "day", date: ${dateField}) < date("${endDate}")`;
      } else if (isEndOfDay) {
        // If it's end of day, include the whole day
        return `dateTrunc(part: "day", date: ${dateField}) >= date("${value}") && dateTrunc(part: "day", date: ${dateField}) < date("${new Date(targetDate.getTime() + 24 * 60 * 60 * 1000).toISOString().split("T")[0]}")`;
      } else {
        // If it's somewhere in the middle, we need to be more careful
        const startDate = value;
        const endDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];
        return `dateTrunc(part: "day", date: ${dateField}) >= date("${startDate}") && dateTrunc(part: "day", date: ${dateField}) < date("${endDate}")`;
      }
    }
    return null;
  };

  // Handle date filters (createdAt, optinAt, optoutAt, birthday, lastMessageReceivedAt)
  const dateFields = {
    createdAt: "createdAt",
    optinAt: "optinAt",
    optoutAt: "optoutAt",
    birthday: "birthday",
    receivedMessageAt: "messages.createdAt",
    clickedUrlAt: "urlClickEvents.createdAt",
    clickedButtonAt: "messages.createdAt",
  };

  // Special handling for received message filter
  if (type === "receivedMessageAt") {
    const dateFilter = generateDateFilter(
      operator,
      value,
      "messages.createdAt",
    );
    if (dateFilter === null) return null;

    // Build the base message criteria
    let baseMessageCriteria = `messages.campaignId != null && messages.direction == "outbound" && (messages.sentAt != null || messages.deliveredAt != null || messages.readAt != null)`;

    // Add date filter if not empty (not "overAllTime")
    if (dateFilter) {
      baseMessageCriteria += ` && ${dateFilter}`;
    }

    // Add campaign filtering if specified
    if (campaignType !== "any" && campaignId) {
      baseMessageCriteria += ` && messages.campaignId == "${campaignId}"`;
    }

    // Apply frequency logic (default to "atLeastOnce" if not specified)
    const freq = frequency || "atLeastOnce";
    if (freq === "atLeastOnce") {
      return `some(messages, where: ${baseMessageCriteria})`;
    } else if (freq === "zeroTimes") {
      return `every(messages, where: !(${baseMessageCriteria}))`;
    }

    return null;
  }

  // Special handling for placed order filter
  if (type === "placedOrderAt") {
    const dateFilter = generateDateFilter(
      operator,
      value,
      "messages.attributedShopifyOrder.processedAt",
    );
    if (dateFilter === null) return null;

    // Build the base message criteria
    let baseMessageCriteria = `messages.attributedShopifyOrderId != null && messages.direction == "outbound" && (messages.sentAt != null || messages.deliveredAt != null || messages.readAt != null)`;

    // Add date filter if not empty (not "overAllTime")
    if (dateFilter) {
      baseMessageCriteria += ` && ${dateFilter}`;
    }

    // Add campaign filtering if specified
    if (campaignType !== "any" && campaignId) {
      baseMessageCriteria += ` && messages.campaignId == "${campaignId}"`;
    }

    // Apply frequency logic (default to "atLeastOnce" if not specified)
    const freq = frequency || "atLeastOnce";
    if (freq === "atLeastOnce") {
      return `some(messages, where: ${baseMessageCriteria})`;
    } else if (freq === "zeroTimes") {
      return `every(messages, where: !(${baseMessageCriteria}))`;
    }

    return null;
  }

  // Special handling for opened message filter
  if (type === "openedMessageAt") {
    const dateFilter = generateDateFilter(operator, value, "messages.readAt");
    if (dateFilter === null) return null;

    // Build the base message criteria
    let baseMessageCriteria = `messages.campaignId != null && messages.direction == "outbound" && messages.readAt != null`;

    // Add date filter if not empty (not "overAllTime")
    if (dateFilter) {
      baseMessageCriteria += ` && ${dateFilter}`;
    }

    // Add campaign filtering if specified
    if (campaignType !== "any" && campaignId) {
      baseMessageCriteria += ` && messages.campaignId == "${campaignId}"`;
    }

    // Apply frequency logic (default to "atLeastOnce" if not specified)
    const freq = frequency || "atLeastOnce";
    if (freq === "atLeastOnce") {
      return `some(messages, where: ${baseMessageCriteria})`;
    } else if (freq === "zeroTimes") {
      return `every(messages, where: !(${baseMessageCriteria}))`;
    }

    return null;
  }

  // Special handling for clicked URL filter
  if (type === "clickedUrlAt") {
    const dateFilter = generateDateFilter(
      operator,
      value,
      "urlClickEvents.createdAt",
    );
    if (dateFilter === null) return null;

    // Build the base urlClickEvent criteria
    let baseUrlClickCriteria = `urlClickEvents.scenario.id != null`;

    // Add date filter if not empty (not "overAllTime")
    if (dateFilter) {
      baseUrlClickCriteria += ` && ${dateFilter}`;
    }

    // Add campaign filtering if specified
    if (campaignType !== "any" && campaignId) {
      if (campaignType === "chatbot") {
        // For chatbot, filter directly on scenario.id
        baseUrlClickCriteria += ` && urlClickEvents.scenario.id == "${campaignId}"`;
      } else {
        // For campaign/automation, filter on scenario.campaign.id
        baseUrlClickCriteria += ` && urlClickEvents.scenario.campaign.id == "${campaignId}"`;
      }
    }

    // Apply frequency logic (default to "atLeastOnce" if not specified)
    const freq = frequency || "atLeastOnce";
    if (freq === "atLeastOnce") {
      return `some(urlClickEvents, where: ${baseUrlClickCriteria})`;
    } else if (freq === "zeroTimes") {
      return `every(urlClickEvents, where: !(${baseUrlClickCriteria}))`;
    }

    return null;
  }

  // Special handling for clicked button filter
  if (type === "clickedButtonAt") {
    const dateFilter = generateDateFilter(
      operator,
      value,
      "messages.createdAt",
    );
    if (dateFilter === null) return null;

    // Build the base message criteria for button clicks
    let baseMessageCriteria = `messages.direction == "inbound" && messages.isQuickReply == true && messages.replyButtonId != null`;

    // Add date filter if not empty (not "overAllTime")
    if (dateFilter) {
      baseMessageCriteria += ` && ${dateFilter}`;
    }

    // Add campaign filtering if specified
    if (campaignType !== "any" && campaignId) {
      if (campaignType === "chatbot") {
        // For chatbot, filter on scenarioId instead of campaignId
        baseMessageCriteria += ` && messages.scenarioId == "${campaignId}"`;
      } else {
        // For campaign/automation, filter via scenario.campaign.id (not campaignId directly)
        baseMessageCriteria += ` && messages.scenario.campaign.id == "${campaignId}"`;
      }
    }

    // Apply frequency logic (default to "atLeastOnce" if not specified)
    const freq = frequency || "atLeastOnce";
    if (freq === "atLeastOnce") {
      return `some(messages, where: ${baseMessageCriteria})`;
    } else if (freq === "zeroTimes") {
      return `every(messages, where: !(${baseMessageCriteria}))`;
    }

    return null;
  }

  if (dateFields[type]) {
    const fieldName = dateFields[type];

    if (operator === "overAllTime") {
      // Treat "over all time" as "more than -1 day ago" (before tomorrow)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const gellyDate = tomorrow.toISOString().split("T")[0];
      return `dateTrunc(part: "day", date: ${fieldName}) < date("${gellyDate}")`;
    }

    if (operator === "moreThan") {
      // "more than X days ago" - contact was created/opted before (older than) the specified date
      const daysAgo = parseInt(value);
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - daysAgo + 1);
      targetDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const gellyDate = targetDate.toISOString().split("T")[0];
      return `dateTrunc(part: "day", date: ${fieldName}) < date("${gellyDate}")`;
    } else if (operator === "lessThan") {
      // "less than X days ago" - contact was created/opted after (more recent than) the specified date
      const daysAgo = parseInt(value);
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - daysAgo + 1);
      targetDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const gellyDate = targetDate.toISOString().split("T")[0];
      return `dateTrunc(part: "day", date: ${fieldName}) >= date("${gellyDate}")`;
    } else if (operator === "after") {
      // "after [date]" - contact was created/opted after the specified date
      const targetDate = new Date(value);
      const isMidnight =
        targetDate.getHours() === 0 &&
        targetDate.getMinutes() === 0 &&
        targetDate.getSeconds() === 0;
      const gellyDate = isMidnight
        ? value
        : new Date(targetDate.getTime() + 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
      return `dateTrunc(part: "day", date: ${fieldName}) >= date("${gellyDate}")`;
    } else if (operator === "before") {
      // "before [date]" - contact was created/opted before the specified date
      const targetDate = new Date(value);
      const isMidnight =
        targetDate.getHours() === 0 &&
        targetDate.getMinutes() === 0 &&
        targetDate.getSeconds() === 0;
      const gellyDate = isMidnight
        ? value
        : new Date(targetDate.getTime() - 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
      return `dateTrunc(part: "day", date: ${fieldName}) < date("${gellyDate}")`;
    } else if (operator === "on") {
      // "on [date]" - contact was created/opted on the exact date
      const endDate = new Date(new Date(value).getTime() + 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      return `dateTrunc(part: "day", date: ${fieldName}) >= date("${value}") && dateTrunc(part: "day", date: ${fieldName}) < date("${endDate}")`;
    }
  }

  return null;
};
